import moment from 'moment-timezone';
import CalendarUtils from './CalendarUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import StaffUtils, { PERMISSIONS } from 'services/StaffUtils';
import PatientUtils from 'services/PatientUtils';
import {
  HISTORY_TYPES,
  ISSUE_TYPES,
  MEDICATION_STATUS,
} from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import AppointmentUtils from 'services/AppointmentUtils';
import _ from 'lodash';
import type {
  Patient,
  Physician,
  Appointment,
  PatientMedication,
  ImmunizationValue,
  AppointmentNote,
  Immunization,
  Prescription,
  PrescriptionMedication,
  PrescriptionMedicationDosing,
  PatientIssue,
  HealthRecords,
  HistoryIssue,
  PharmacyDirectory,
} from 'generated/api';
import { PatientData } from '../careand-redux/slices/healthRecords';
import { FilterKeys } from './TypeUtils';

const BLOOD_TYPES = ['N/A', 'O-', 'O+', 'A-', 'A+', 'B-', 'B+', 'AB-', 'AB+'] as const;

const TIMEUNIT_TO_DAYS: Record<string, number> = {
  minute: 1 / 24 / 60,
  minutes: 1 / 24 / 60,
  hour: 1 / 24,
  hours: 1 / 24,
  day: 1,
  days: 1,
  weeks: 7,
  week: 7,
  months: 30,
  month: 30,
};

const PILLS_PER_DAY: Record<string, number> = {
  daily: 1,
  'every other day': 0.5,
  qd: 1,
  qod: 0.5,
  qad: 0.5,
  bid: 2,
  tid: 3,
  qid: 4,
  q4h: 24 / 4,
  'q4-6h': 24 / 4,
  q6h: 24 / 6,
  qwk: 1 / 7,
  qhs: 1,
  qpm: 1,
  qam: 1,
};

const ORIGIN_SRC: Record<string, string | [string, string]> = {
  'Apple Health': ['fab', 'apple'],
  Practitioner: 'user-doctor',
  Lab: 'vial',
  Device: 'mobile',
  'Android Health': ['fab', 'android'],
};

interface GTPALValues {
  g?: string | number;
  t?: string | number;
  p?: string | number;
  a?: string | number;
  l?: string | number;
  [key: string]: string | number | undefined;
}

interface MacroData {
  'T#'?: string;
  'D#'?: string;
  'H#'?: string | number;
  'W#'?: string | number;
  'A#'?: string;
  'M#'?: string;
  [key: string]: string | number | undefined;
}

interface ReferralOptions {
  reason_for_referral?: string;
  further_discussion?: string;
}

class CPPUtils {
  calculateBMI(height?: number | string, weight?: number | string): string | undefined {
    if (height && weight) {
      let bmi = Number(weight) / (Number(height) / 100.0) ** 2;
      return parseFloat(String(bmi)).toFixed(1);
    }
    return undefined;
  }

  getMetricOriginSrc(origin?: string): string | [string, string] {
    if (!origin) return 'chart-line';
    return ORIGIN_SRC[origin] || 'chart-line';
  }

  orderMetrics(metrics: any[]): any[] {
    let copy = _.sortBy(PerformanceUtils.clone(metrics), 'metric.name');
    let priority = ['Lab', 'Practitioner'];
    let result: any[] = [];
    priority.forEach((origin) => {
      result = [...result, ...copy.filter((d) => d.origin === origin)];
      copy = copy.filter((d) => d.origin !== origin);
    });
    return [...result, ...copy];
  }

  getBloodTypes(): readonly string[] {
    return BLOOD_TYPES;
  }

  getFormName(icon: string, patientId: string, extraStr = ''): string {
    return `cpp${icon}${patientId}${extraStr}`;
  }

  getAppointmentFormName(patientId: string): string {
    return `NewAppointment-${patientId}`;
  }

  getGTPAL(values: GTPALValues = {}): string {
    if (Object.keys(values).length === 0) return '';

    const order = ['g', 't', 'p', 'a', 'l'];
    return order
      .filter((key) => values[key])
      .map((key) => {
        return `${key.toUpperCase()}${values[key] || ''}`;
      })
      .join(' ');
  }

  getNumberOfGTPALValues(values: GTPALValues = {}): number {
    return Object.values(values).filter((value) => !!value).length;
  }

  getAppointmentNoteEditElement(note: AppointmentNote, draftId?: string): any {
    let metrics: Record<string, string | number> = {};
    (note.value.metrics || []).forEach((metric) => {
      metrics[metric.name] = metric.value;
    });

    return {
      itemId: note.id,
      authorId: note.author_id,
      ...note.value,
      ...metrics,
      draftId,
      item_type: note.item_type,
      health_records: note.health_records,
      documents: note.documents,
      originalNote: note,
    };
  }

  getImmunizationEditElement(immunization: Immunization): any {
    return {
      itemId: immunization.id,
      ...immunization.value,
    };
  }

  getAppointmentEditElement(appointment: Appointment): any {
    return {
      ...appointment,
      time: appointment.date,
    };
  }

  getImmunizationDescription(value: ImmunizationValue): string {
    let text = value.name || '';

    if (value.description) text += ' for ' + value.description;
    if (value.prescription) text += '. Rx: ' + value.prescription;

    if (value.lot) text += ', lot ' + value.lot;

    if (value.given_date) text += '. Given in ' + CalendarUtils.format(value.given_date);

    if (this.isPendingImmunization({ value } as Immunization)) {
      text += `. Due by ${CalendarUtils.format(value.scheduled_date!)}`;
    }

    return text;
  }

  getPrescriptionMedicationSummary(
    medication?: PrescriptionMedication,
    ignoreName = false,
  ): string {
    if (!medication) {
      return '';
    }
    let prnDisabled = !medication.as_needed && !medication.prn;

    const describeInstruction = (
      instruction: PrescriptionMedicationDosing,
      includeConnector = true,
    ): string => {
      return _.compact([
        instruction.dosage,
        instruction.dosage_unit,
        (instruction as any).route,
        instruction.frequency,
        instruction.time ? `for ${instruction.time}` : undefined,
        instruction.time_unit,
        includeConnector ? instruction.connector : undefined,
      ]).join(' ');
    };

    const describeDosageInstructions = (route?: string): string => {
      if (medication.dosing_instructions?.length! > 0) {
        return medication
          .dosing_instructions!.map((m: any, i: number) =>
            describeInstruction({ ...m, route }, i < medication.dosing_instructions!.length - 1),
          )
          .join('\n');
      }
      return describeInstruction(medication);
    };

    return _.compact([
      ignoreName ? undefined : medication.name,
      ignoreName ? undefined : !prnDisabled ? 'PRN ' : '',
      describeDosageInstructions(ignoreName ? undefined : medication.route),
      medication.refills ? 'refills ' + medication.refills + ' ' : '',
      medication.quantity ? 'Quantity: ' + medication.quantity + ' ' : '',
      medication.quantity_unit,
      medication.comments,
    ])
      .join(' ')
      .trim();
  }

  getTranscriptionAppointment(
    appointments: Appointment[] = [],
    ownPhysician: Physician,
    permissions: string[] = [],
  ): Appointment | undefined {
    const isIntern = ownPhysician.intern_of;
    const ids = [ownPhysician.id, ownPhysician.intern_of];
    return appointments?.find(
      ({ date, physician_id, has_transcription, status }) =>
        (ids.includes(physician_id) || isIntern || permissions.includes(PERMISSIONS.super_admin)) &&
        has_transcription &&
        (CalendarUtils.isSameDay(moment.unix(date!), moment()) ||
          status === AppointmentUtils.getAppointmentStatus().checked_in),
    );
  }

  isPendingImmunization(immunization: Immunization): boolean {
    return (
      immunization.value.scheduled_date !== undefined && immunization.value.given_date === undefined
    );
  }

  isOverdueImmunization(immunization: Immunization): boolean {
    return (
      this.isPendingImmunization(immunization) &&
      immunization.value.scheduled_date! < moment().unix()
    );
  }

  isTranscribedImmunization(immunization: Immunization): boolean {
    return immunization.value.transcribed === true;
  }

  isGivenImmunization(immunization: Immunization): boolean {
    return !!immunization.value.given_date;
  }

  isWithinOneYearImmunization(immunization: Immunization): boolean {
    return (
      !this.isPendingImmunization(immunization) ||
      moment().add(1, 'year').isAfter(moment.unix(immunization.value.scheduled_date!))
    );
  }

  getImmunizationIcon(immunization: Immunization): {
    src?: string;
    className?: string;
    fontAwesomeStyle?: string;
  } {
    if (!this.isPendingImmunization(immunization)) return { src: 'check', className: 'green' };
    else if (this.isOverdueImmunization(immunization))
      return {
        src: 'circle-exclamation',
        className: 'red',
        fontAwesomeStyle: 'fas',
      };
    else if (this.isWithinOneYearImmunization(immunization))
      return { src: 'calendar-exclamation', className: 'blue' };
    else if (this.isPendingImmunization(immunization)) return { src: 'clock' };
    return {};
  }

  getAdministeredDrugIcon(drug: Immunization): {
    src?: string;
    className?: string;
    fontAwesomeStyle?: string;
  } {
    if (this.isOverdueImmunization(drug))
      return {
        src: 'circle-exclamation',
        className: 'red',
        fontAwesomeStyle: 'fas',
      };
    else if (this.isPendingImmunization(drug)) return { src: 'clock' };
    return {};
  }

  getImmunizationIconDescription(immunization: Immunization): string | undefined {
    if (this.isTranscribedImmunization(immunization)) return 'Transcribed';
    else if (!this.isPendingImmunization(immunization))
      return 'Administered by Care& Family Health';
    else if (this.isOverdueImmunization(immunization)) return 'Overdue';
    else if (this.isWithinOneYearImmunization(immunization)) return 'Coming up soon';
    else if (this.isPendingImmunization(immunization)) return 'Due';
    return undefined;
  }

  filterScheduledAndOverdueImmunizations(immunizations: Immunization[]): Immunization[] {
    let result = immunizations.filter((immunization) => {
      return (
        this.isOverdueImmunization(immunization) ||
        (this.isPendingImmunization(immunization) && this.isWithinOneYearImmunization(immunization))
      );
    });

    return PerformanceUtils.sortBy(result, ['value.scheduled_date'], false);
  }

  /**
   * @returns {PatientMedication[]}
   * */
  getActiveMedications = (medications: PatientMedication[]): PatientMedication[] => {
    return medications.filter((medication) => this.isActiveMedication(medication));
  };

  getInactiveMedications(medications: PatientMedication[]): PatientMedication[] {
    return medications.filter((medication) => !this.isActiveMedication(medication));
  }

  isActiveMedication = (medication: PatientMedication): boolean => {
    if (!this.isHealthRecordItemEnabled(medication as any)) return false;
    let { status } = medication.value!;

    return (
      status !== MEDICATION_STATUS.discontinue.name &&
      status !== MEDICATION_STATUS.stop.name &&
      status !== MEDICATION_STATUS.hold.name
    );
  };

  getActiveHealthRecords<K extends FilterKeys<HealthRecords, PatientIssue[] | undefined>>(
    healthRecords: HealthRecords | undefined,
    key: K,
    filter: (value: any) => boolean = () => true,
    describe: (value: any) => string = (value) => value.name,
  ): string[] {
    return (
      healthRecords?.[key]?.reduce((acc: string[], item) => {
        if (this.isHealthRecordItemEnabled(item) && filter(item.value)) {
          const description = describe(item.value);
          if (description?.trim()) {
            acc.push(description);
          }
        }
        return acc;
      }, []) ?? []
    );
  }

  getPreviousPharmacies(
    prescriptions: Prescription[] = [],
    defaultPharmacy?: PharmacyDirectory | null,
  ): any[] {
    let pharmacies = prescriptions.reduce((acc: any[], prescription) => {
      if (prescription.pharmacy !== undefined) acc.push(prescription.pharmacy);
      return acc;
    }, []);

    // The patient's default pharmacy is now a reference resolved by the caller
    // (useDefaultPharmacy) rather than an embedded snapshot.
    if (defaultPharmacy) {
      pharmacies.unshift(defaultPharmacy);
    }

    return _.uniqBy(pharmacies, 'id') || [];
  }

  /**
   * The fax number a prescription should actually be sent to: the manually
   * curated `preferred_fax` override when set, otherwise the OCP-listed `fax`.
   * Mirrors the backend's faxing logic so the UI always shows/uses the same
   * destination the fax is sent to. Returns undefined when neither is set.
   */
  getPharmacyFax(
    pharmacy?: { fax?: string | null; preferred_fax?: string | null } | null,
  ): string | undefined {
    return pharmacy?.preferred_fax || pharmacy?.fax || undefined;
  }

  isHealthRecordItemEnabled(item: PatientIssue): boolean {
    return !item.history || item.history[0]?.action !== 'Removed';
  }

  // Prescribe-type statuses represent an actual Rx being issued, as opposed to a
  // status review (Continue/Hold/Discontinue/Stop). 'Prescribed' is what the
  // server records when an e-prescription is issued (see server MEDICATION_STATUS);
  // 'Renew (Rx)' is kept defensively — today the web renew action re-enters the
  // prescribe flow (landing as 'Prescribed'), but if a renew is ever recorded as
  // its own status this still counts it. Kept narrow so the 'Last prescribed'
  // label is never asserted on a non-prescribe event.
  getMedicationHistorySummary(item: { history?: any[]; value?: any }, limit = 10) {
    const PRESCRIBE_STATUSES = ['Prescribed', 'Renew (Rx)'];
    const history = Array.isArray(item?.history) ? item.history : [];

    // Newest first. The server prepends newest-first today, but sort defensively
    // so a drifted stored order can't push an old entry to the top.
    const sorted = [...history].sort((a, b) => (b?.date || 0) - (a?.date || 0));

    const shown = sorted.slice(0, limit);
    const remaining = Math.max(0, sorted.length - limit);

    const prescribeEntry = sorted.find((h) => PRESCRIBE_STATUSES.includes(h?.value?.status));
    const newest = sorted[0];

    return {
      shown,
      remaining,
      // Only a genuine prescribe/renew event; undefined otherwise.
      lastPrescribed: prescribeEntry?.date as number | undefined,
      // The newest entry regardless of status, for a neutral 'Most recent' line.
      mostRecent: newest
        ? { date: newest.date as number, status: (newest.value?.status || 'Continue') as string }
        : undefined,
    };
  }

  calculateDrugLastsUntil(
    medication: Partial<PrescriptionMedication>,
    startDate = moment().unix(),
  ): number | undefined {
    let dosingInstructions =
      medication.dosing_instructions?.length! > 0
        ? medication.dosing_instructions
        : [{ time: medication.time, time_unit: medication.time_unit }];

    const refills = parseFloat(String(medication.refills || '0')) || 0;

    if (!dosingInstructions || dosingInstructions?.length === 0) return;

    // Calculate total duration from all dosing instructions
    let totalDuration = 0;
    let baseUnit = 'days'; // Default base unit

    // Find the most common time unit or use the first valid one as base
    const timeUnits = dosingInstructions
      .map((instruction: any) => instruction.time_unit)
      .filter((unit: any) => unit);

    if (timeUnits.length > 0) {
      baseUnit = timeUnits[0]!.trim(); // Use the first time unit as base
    }

    // Sum up all durations, converting to base unit if needed
    for (const instruction of dosingInstructions) {
      const time = parseFloat(String(instruction.time || 0));
      const timeUnit = instruction.time_unit?.trim();

      if (time && timeUnit) {
        if (timeUnit === baseUnit) {
          totalDuration += time;
        } else {
          // Convert different units to base unit
          const convertedTime = this._convertTimeUnit(time, timeUnit, baseUnit);
          if (convertedTime !== null) {
            totalDuration += convertedTime;
          }
        }
      }
    }

    if (totalDuration <= 0) return;

    // Apply refills logic: total duration * (refills + 1)
    let finalDuration = totalDuration * (refills + 1);
    let result = moment
      .unix(startDate)
      .add(finalDuration, baseUnit as any)
      .unix();

    if (!Number.isNaN(result)) return result;
  }

  // Helper method to convert between time units
  _convertTimeUnit(value: number, fromUnit: string, toUnit: string): number | null {
    if (fromUnit === toUnit) return value;

    const fromMultiplier = TIMEUNIT_TO_DAYS[fromUnit];
    const toMultiplier = TIMEUNIT_TO_DAYS[toUnit];

    if (fromMultiplier === undefined || toMultiplier === undefined) return null;

    const fromDays = value * fromMultiplier;
    return fromDays / toMultiplier;
  }

  calculateMedicationQuantity(medication: Partial<PrescriptionMedication>): number | undefined {
    let { prn, route, dosing_instructions = [] } = medication;

    if (prn) return;

    const calculateOne = (instruction: PrescriptionMedicationDosing): number | undefined => {
      let {
        frequency = '',
        time = '',
        time_unit = '',
        dosage = '',
        dosage_unit = '',
      } = instruction;
      if (frequency && time && time_unit && route && dosage_unit) {
        frequency = frequency.trim().toLowerCase();
        time = time.trim().toLowerCase();
        time_unit = time_unit.trim().toLowerCase();
        route = route.trim().toLowerCase();
        dosage = (dosage || '').trim().toLowerCase();
        dosage_unit = (dosage_unit || '').trim().toLowerCase();

        if (route !== 'po') return;
        if (dosage_unit && !dosage_unit.includes('tab') && !dosage_unit.includes('capsule')) return;

        let pillsPerDay = PILLS_PER_DAY[frequency];
        if (!pillsPerDay) return;
        let days = TIMEUNIT_TO_DAYS[time_unit];
        if (!days) return;

        let dosageAmount = 1;

        if (dosage) {
          if (dosage.includes('/')) {
            let split = dosage.split('/');
            let numerator = parseFloat(split[0]!);
            let denominator = parseFloat(split[1]!) || 1;
            dosageAmount = numerator / denominator;
          } else dosageAmount = parseFloat(dosage);
        }

        return Math.ceil(dosageAmount * pillsPerDay * days * parseFloat(time));
      }
    };

    if (dosing_instructions?.length! > 0) {
      let totalPills = 0;
      for (const instruction of dosing_instructions) {
        totalPills += calculateOne(instruction) || 0;
      }
      return totalPills;
    } else {
      return calculateOne({
        frequency: medication.frequency,
        time: medication.time,
        time_unit: medication.time_unit,
        dosage: medication.dosage,
        dosage_unit: medication.dosage_unit,
      });
    }
  }

  calculateDueDate(lastPeriod: number): number {
    let momentDate = moment.unix(lastPeriod);
    let dueDate = momentDate.add(280, 'days');
    return dueDate.unix();
  }

  calculateWeeksPregnant(lastPeriod: number): string {
    let momentDate = moment.unix(lastPeriod);
    let weeksPassed = moment().diff(momentDate, 'weeks');
    let daysPassed = moment().add(-weeksPassed, 'weeks').diff(momentDate, 'days');
    let result = `${weeksPassed}w`;
    if (daysPassed >= 1) result += ` + ${daysPassed}d`;
    return result;
  }

  checkMacro(value: string | undefined, macroData: MacroData = {}): string | undefined {
    if (!value) return value;

    if (value.includes('T#') && !macroData['T#']) {
      value = value.replace('T#', CalendarUtils.format(moment().unix(), 'h:mm A'));
    } else if (value.includes('D#') && !macroData['D#']) {
      value = value.replace('D#', CalendarUtils.format(moment().unix(), 'YYYY/MM/DD'));
    } else {
      Object.keys(macroData).forEach((key) => {
        if (value!.includes(key) && macroData[key]) {
          value = value!.replace(key, String(macroData[key]));
        }
      });
    }

    return value;
  }

  getPatientMeasurements(patientHealthRecords: PatientData): {
    height: number | undefined;
    weight: number | undefined;
  } {
    const patientSummary = _.orderBy(
      patientHealthRecords?.dataSummary || [],
      'last_value.date',
    ).reverse();
    let weight = patientSummary?.find((val) => val.metric!.id === 'weight')?.last_value?.value1;
    let weightNum = weight && parseFloat(weight);
    let height = patientSummary?.find((val) => val.metric!.id === 'height')?.last_value?.value1;
    let heightNum = height && parseFloat(height);

    return { height: heightNum || undefined, weight: weightNum || undefined };
  }

  getMacroData(patientHealthRecords: PatientData | undefined): MacroData {
    const patientSummary = patientHealthRecords?.dataSummary || [];
    let weight = patientSummary?.find((val) => val.metric!.id === 'weight')?.last_value?.value1;
    let height = patientSummary?.find((val) => val.metric!.id === 'height')?.last_value?.value1;

    let allergies =
      (patientHealthRecords?.healthRecords?.allergies || [])
        .filter((allergy) => this.isHealthRecordItemEnabled(allergy))
        .map((allergy) => allergy.value.name)
        .join(', ') || 'NKA';

    let medications = (patientHealthRecords?.healthRecords?.medications || [])
      .filter((medication) => this.isHealthRecordItemEnabled(medication))
      .map((medication) => medication.value.prescription_medication?.name || medication.value.name)
      .join(', ');

    return { 'H#': height, 'W#': weight, 'A#': allergies, 'M#': medications };
  }

  describeMedication(item: PatientMedication, includeComments = false): string {
    let valueString = '';

    let medication = item.value;

    let drugName =
      medication?.prescription_medication?.drugbank_product?.prescribable_name ||
      medication?.prescription_medication?.drugbank_product?.name ||
      medication?.prescription_medication?.name ||
      medication?.name;

    let routeIndex = -1;
    let route = medication?.prescription_medication?.drugbank_product?.route;
    if (route) {
      routeIndex = drugName.indexOf(route);
      if (routeIndex > -1) {
        drugName = drugName.substring(0, routeIndex);
      }
    }

    if (medication?.prescription_medication) {
      const { dosage, dosage_unit, frequency } = medication.prescription_medication;
      valueString += drugName;
      let takeInstructions = [dosage, dosage_unit, frequency].filter((d) => d).join(' ');
      if (takeInstructions) valueString += ` - ${takeInstructions}`;
    } else {
      valueString += drugName;
    }

    if (medication.prescription_medication?.comments && includeComments) {
      valueString += ` - ${medication.prescription_medication?.comments}`;
    }

    return valueString;
  }

  getLatestNotes(notes: AppointmentNote[] = [], count = 15): string {
    return notes
      .slice()
      .reverse()
      .slice(0, count)
      .map(
        ({ value }) => `${CalendarUtils.fromNow(value.date!)}

${value.value}`,
      )
      .join('\n ----- \n');
  }

  convertMedicationDosingInformation(
    prescriptionMedication: PrescriptionMedication,
  ): PrescriptionMedication {
    let medication = PerformanceUtils.clone(prescriptionMedication);
    if (medication.as_needed) medication.prn = true;

    // Convert old format to new dosing_instructions format if needed
    if (
      !medication.dosing_instructions?.length &&
      (medication.dosage || medication.time || medication.frequency)
    ) {
      medication.dosing_instructions = [
        {
          dosage: medication.dosage || '',
          dosage_unit: medication.dosage_unit || '',
          frequency: medication.frequency || '',
          time: medication.time || '',
          time_unit: medication.time_unit || '',
          connector: 'THEN',
        },
      ];

      // Clear old format fields to avoid confusion
      delete medication.dosage;
      delete medication.dosage_unit;
      delete medication.frequency;
      delete medication.time;
      delete medication.time_unit;
    }
    return medication;
  }

  getReferralCPPInfo(records?: HealthRecords): string {
    let keys = [
      'medical_issues',
      'medications',
      'allergies',
      'history',
      'preventative_screenings',
    ] as const;

    let newText = ''; //text + '\n';

    keys.forEach((key) => {
      let keyString = key.replace('_', ' ');
      keyString = keyString.charAt(0).toUpperCase() + keyString.slice(1) + ':';

      let data = records?.[key] || [];

      if (key === 'history') {
        // don't add a title
        data = PerformanceUtils.sortBy(data, ['value.history_type'], false).filter(
          (item: HistoryIssue) =>
            item.value.history_type === HISTORY_TYPES.family_history ||
            item.value.history_type === HISTORY_TYPES.surgical_history,
        );
      } else if (key === 'medications') {
      } else if (key === 'medical_issues') {
        data = PerformanceUtils.clone(data).map((item: PatientIssue) => {
          if (!item.value.issue_type) item.value.issue_type = (ISSUE_TYPES as any).current;
          return item;
        });
        data = PerformanceUtils.sortBy(data, ['value.issue_type'], false);
      }

      if (
        data.length > 0 &&
        key !== 'history' &&
        key !== 'medical_issues' &&
        key !== 'medications'
      ) {
        newText += '\n' + keyString + '\n';
      } else {
        if (key === 'allergies') newText += '\n' + keyString + ' NKDA\n';
      }

      let currentHistoryType: string | undefined;

      if (key === 'medications') {
        //filter active medications, minus those whose supply has run out
        let active = this.getActiveMedications(data as PatientMedication[]).filter(
          (medication) => !this.hasMedicationRunOut(medication),
        );
        const describeMedication = this.describeMedication.bind(this);

        function printMedications(title: string, medications: PatientMedication[] = []): void {
          if (medications.length > 0) {
            newText += `\n${title}:\n`;
            medications.forEach((item) => {
              let description = describeMedication(item);
              if (description) newText += `${description}\n`;
            });
          }
        }

        printMedications('Active Medications', active as any);
      } else {
        if (key === 'medical_issues' && data.length > 0) newText += '\nPMHx:';

        data.forEach((item) => {
          const { name, details, specialty, history_type, issue_type, date, notes } =
            item.value as any;

          if (this.isHealthRecordItemEnabled(item)) {
            let valueString = '';

            let type = history_type || issue_type;

            if (type && type !== currentHistoryType) {
              currentHistoryType = type;
              valueString += `\n${type}:\n`;
            }

            if (type) valueString += '-';

            valueString += name;
            if (details) valueString += `, ${details}`;
            if ((key as any) !== 'medications' && notes) valueString += `, ${notes}`;
            if (specialty) valueString += ` (${specialty})`;
            if (date) {
              valueString += ` (${moment.unix(date).format('YYYY/MM/DD')})`;
            }

            newText += `${valueString}\n`;
          }
        });
      }
    });
    return newText;
  }

  /**
   * Mirrors the left-sidebar colour logic (CPPMedicationListElement's
   * `medication-ran-out`): a medication whose computed supply end date is in
   * the past. Medications with no computable end date are NOT considered run
   * out. Note the same start-date fallback to the newest history entry the
   * sidebar uses.
   * */
  hasMedicationRunOut(medication: PatientMedication): boolean {
    const { prescription_medication, date } = medication.value || {};
    if (!prescription_medication) return false;

    const startDate = date || (medication.history?.[0]?.date as number | undefined);
    const lastsUntil = this.calculateDrugLastsUntil(prescription_medication as any, startDate);

    return !!lastsUntil && lastsUntil < moment().unix();
  }

  /**
   * @param {import('generated/api').PatientMedication} medication
   * @param {number} daysThreshold - a change in days to the last_until
   * @returns {[true, number] | [false, number | undefined]}
   * */
  medicationStillLasts(
    medication: PatientMedication,
    daysThreshold = 0,
  ): [true, number] | [false, number | undefined] {
    const date = this.calculateDrugLastsUntil(
      medication.value?.prescription_medication as any,
      medication.value?.date,
    );

    if (!date) {
      return [false, undefined];
    }

    const cutoffDate = date && moment.unix(date).add(daysThreshold, 'days').unix();

    return [!!date && moment().unix() < cutoffDate, date];
  }

  /**
   * @param {import('generated/api').PatientMedication} medication
   * */
  medicationAllowsRefillRequests(medication: PatientMedication): boolean {
    return !medication.value?.prescription_medication?.disable_refills;
  }

  getReferralText(
    patient: Patient,
    physician: Physician,
    cppText: string,
    { reason_for_referral, further_discussion }: ReferralOptions = {},
  ): string {
    let age = PatientUtils.getAge(patient);
    let gender = (PatientUtils.getGender(patient) || '').toLowerCase();
    let physicianName = StaffUtils.getName(physician);

    return `Dear Colleague:

Please see this pleasant ${age} old ${gender} for ${reason_for_referral ?? '__________________'}.${
      further_discussion ? `\n\n${further_discussion}\n` : ''
    }
${cppText}
Please see attachments for recent investigations completed.

Thank you for your involvement in this client's care. We look forward to hearing from you.

If you require any additional information, please do not hesitate to contact us immediately.

Kind regards,
${physicianName}`;
  }
}

export default new CPPUtils();
